// API client for the Vendor OS GAS backend.
//
// Request shape (matches Code.gs doPost):
//   POST <VITE_GAS_API_URL>  Content-Type: text/plain;charset=utf-8
//   { fn: 'api_listVendors', args: {...}, token: '<id-token-or-magic-link>' }
//
// text/plain avoids the CORS preflight that an Authorization header or
// application/json would trigger — GAS Web Apps don't answer OPTIONS.
//
// When VITE_GAS_API_URL is unset, we route to an in-memory mock backed by
// ./prototype/data.jsx so the SPA runs before the GAS backend is deployed.

import { SUPPLIERS, PENDING_APPROVALS, MY_BILLS } from './prototype/data.jsx';
import { getCurrentToken, forceReauth, hasStaffToken, clearStaffSession } from './auth.js';

const GAS_URL = import.meta.env.VITE_GAS_API_URL || '';
const useMock = !GAS_URL || GAS_URL.includes('REPLACE_ME');

// When the backend rejects the token (expired session / invalid magic link),
// we don't want to keep flashing a red banner — we bounce the user straight to
// a fresh sign-in. forceReauth() reloads, so guard against firing it more than
// once when a burst of parallel calls all fail at the same moment.
let reauthInFlight = false;
function isSessionExpiredError(msg) {
  const m = String(msg || '').toLowerCase();
  // Only token-verification failures are prefixed 'unauthorized:' by Code.gs.
  // Role/permission errors thrown inside a handler are NOT — they stay banners.
  if (m.indexOf('unauthorized') === -1) return false;
  // Bounce only on genuine expiry/invalid-token cases. Leave "deactivated" /
  // "not on allowlist" / "wrong audience" as banners — re-login can't fix those
  // and would just loop the user through the sign-in screen.
  return /expired|no token|tokeninfo rejected|bad signature|malformed magic|bad payload/.test(m);
}
// A Google sign-in the backend won't authorize as staff ("not on allowlist").
// The most common real cause is a SUPPLIER who used "Sign in with Google"
// (their email is a Google Workspace account) instead of opening the portal
// link we emailed them — Google authenticates them, but they're not on the
// staff allowlist, so every call fails and the app is stranded with red errors.
let staffBounceInFlight = false;
function isStaffNotAuthorized(msg) {
  const m = String(msg || '').toLowerCase();
  return m.indexOf('unauthorized') !== -1 && m.indexOf('not on allowlist') !== -1;
}
function maybeRecoverStrandedStaff(msg) {
  if (staffBounceInFlight || !isStaffNotAuthorized(msg)) return;
  if (!hasStaffToken()) return;   // only a Google (staff) token can be stranded this way
  // Drop the dead staff token (keeping any vendor token) and return to the
  // login screen with a Supplier-mode notice. This never loops: Google won't
  // auto re-sign-in (auto_select:false), so the reload lands on a clean login.
  // The 20s sessionStorage guard is belt-and-suspenders against a burst of
  // parallel failing calls all trying to bounce at once.
  try {
    const last = Number(sessionStorage.getItem('vendoros:notstaff_at') || 0);
    const now = +new Date();
    if (last && now - last < 20000) return;
    sessionStorage.setItem('vendoros:notstaff_at', String(now));
    sessionStorage.setItem('vendoros:not_staff', '1');
  } catch (_) { /* sessionStorage blocked — staffBounceInFlight still guards this load */ }
  staffBounceInFlight = true;
  clearStaffSession();
  window.location.reload();
}

function maybeForceReauth(msg) {
  if (reauthInFlight || !isSessionExpiredError(msg)) return;
  // Loop-breaker. forceReauth() clears tokens and reloads. Normally the next
  // boot has no token → login screen → no more API calls → done. But if a token
  // is somehow re-applied on reload (a restored tab re-running its ?t= magic
  // link, another tab re-seeding the token, etc.) an unguarded reload would loop
  // forever → blank/flashing page. So allow at most ONE auto-bounce per short
  // window (persisted in sessionStorage so it survives the reload); after that,
  // stop reloading and let the call fail normally (banner / empty state).
  try {
    const last = Number(sessionStorage.getItem('vendoros:reauth_at') || 0);
    const now = +new Date();
    if (last && now - last < 20000) return;       // bounced <20s ago → don't loop
    sessionStorage.setItem('vendoros:reauth_at', String(now));
  } catch (_) { /* sessionStorage blocked — module-level reauthInFlight still guards this load */ }
  reauthInFlight = true;
  forceReauth();
}

/**
 * A response that isn't a doPost reply at all.
 *
 * A GAS web app answers /exec POSTs through a redirect to
 * script.googleusercontent.com. When the script is cold or busy that leg can be
 * served as a GET instead — which runs doGet and returns the health payload
 * ({ok, version, at}). Measured on the live backend: a listVendors POST came
 * back after 37s carrying exactly that.
 *
 * The old code only looked at `json.error`, so the health payload sailed
 * through as a success and `json.data` was `undefined` — the caller then wrote
 * `undefined` over good state. That is the "I approved it, File B got the row,
 * but the invoice didn't move until I refreshed" bug: the write worked, the
 * re-read silently returned nothing.
 *
 * Every genuine doPost reply carries exactly one of `data` or `error`.
 */
class TransportError extends Error {
  constructor(fn, detail) {
    super(`API ${fn} — ${detail}`);
    this.name = 'TransportError';
    this.fn = fn;
    this.isTransport = true;
  }
}

function isDoPostReply(json) {
  return !!json && typeof json === 'object' && ('data' in json || 'error' in json);
}

// Big replies come back gzipped (see Code.gs jsonOut_): { gz: <base64> }.
// We only ask for that (gz:true in the request) when we can inflate it.
const CAN_INFLATE = typeof DecompressionStream === 'function';
async function inflateReply(json) {
  if (!json || typeof json !== 'object' || typeof json.gz !== 'string') return json;
  const bin = atob(json.gz);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('gzip'));
  const text = await new Response(stream).text();
  return JSON.parse(text);
}

// Reads are safe to replay; writes are not (a retried transition would
// double-post to the audit log and could overwrite paid_at). Anything not on
// this list surfaces the transport failure to the user instead of guessing.
// Reads, plus the writes the backend made idempotent on 2026-09-16:
//  - uploadFile: a replay leaves at most one orphaned staged file (trashed daily)
//  - submitInvoice: same supplier + invoice no + amount within 15 min → existing row
//  - uploadTaxInvoice: file ids already attached are skipped
//  - updatePortalProfile: same fields, same result
//  - login: a lost reply just means a second session row
const RETRYABLE = /^api_(list|get|bootstrap|whoami|portalMe|portalBootstrap|uploadFile|submitInvoice|uploadTaxInvoice|updatePortalProfile|login)$|^api_(list|get)/;
const MAX_ATTEMPTS = 3;
const sleep = (ms) => new Promise(r => setTimeout(r, ms));

// A cold Apps Script instance takes 20–40 s to answer; a hung one never does.
// Before 2026-09-16 there was no timeout at all (a stuck call spun forever)
// and the retry waited 400/800 ms — far less than a cold start, so the retry
// just landed on the same cold instance. Now: abort at 60 s (3 min for file
// uploads, which legitimately carry megabytes), retry after 2 s then 5 s.
const CALL_TIMEOUT_MS   = 60000;   // a cold staff bootstrap was measured at 46 s on 2026-09-16
const UPLOAD_TIMEOUT_MS = 180000;
const RETRY_DELAYS_MS   = [2000, 5000];
const UPLOAD_FNS = /^api_(uploadFile|uploadKycDoc|uploadTaxInvoice|submitInvoice|registerVendor)$/;

// "Slow call" signal for the UI. Any call still pending after SLOW_AFTER_MS
// flips the flag on; it flips off when nothing slow is pending. app.jsx
// renders a small "waking up the server" strip from it, so a 25 s cold start
// reads as "please wait" instead of "the app is broken".
const SLOW_AFTER_MS = 4000;
const slowListeners = new Set();
const slowPending = new Set();
function emitSlow() { const on = slowPending.size > 0; slowListeners.forEach(cb => { try { cb(on); } catch (_) {} }); }
export function subscribeSlow(cb) { slowListeners.add(cb); return () => slowListeners.delete(cb); }

async function call(fn, args = {}) {
  return callWith(fn, args, getCurrentToken());
}

/** Call with an explicit token (or null for the public/anonymous endpoints). */
async function callWith(fn, args, token) {
  if (useMock) return mockCall(fn, args);
  const attempts = RETRYABLE.test(fn) ? MAX_ATTEMPTS : 1;
  let lastErr;
  for (let attempt = 1; attempt <= attempts; attempt++) {
    try {
      return await callOnce(fn, args, token);
    } catch (err) {
      lastErr = err;
      // Only transport-level trouble is worth replaying. A real backend error
      // ("role required", "invoice not found") will say the same thing again.
      if (!err.isTransport || attempt === attempts) break;
      await sleep(RETRY_DELAYS_MS[attempt - 1] || RETRY_DELAYS_MS[RETRY_DELAYS_MS.length - 1]);
    }
  }
  if (lastErr && lastErr.isTransport && !RETRYABLE.test(fn)) {
    // A write whose reply we never saw. It may well have been applied — say so,
    // because "failed" would be a lie and could prompt a duplicate action.
    throw new Error(
      `${lastErr.message}. The change may still have gone through — refresh before trying again.`
    );
  }
  throw lastErr;
}

async function callOnce(fn, args, token) {
  const timeoutMs = UPLOAD_FNS.test(fn) ? UPLOAD_TIMEOUT_MS : CALL_TIMEOUT_MS;
  const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
  const killer = ctrl ? setTimeout(() => ctrl.abort(), timeoutMs) : null;
  const slowKey = {};
  const slowTimer = setTimeout(() => { slowPending.add(slowKey); emitSlow(); }, SLOW_AFTER_MS);
  let res;
  try {
    res = await fetch(GAS_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify({ fn, args, token: token || null, gz: CAN_INFLATE }),
      signal: ctrl ? ctrl.signal : undefined,
    });
  } catch (netErr) {
    const aborted = netErr && (netErr.name === 'AbortError');
    throw new TransportError(fn, aborted
      ? `no answer from the server after ${Math.round(timeoutMs / 1000)} s`
      : `network error (${netErr.message})`);
  } finally {
    if (killer) clearTimeout(killer);
    clearTimeout(slowTimer);
    if (slowPending.delete(slowKey)) emitSlow();
  }
  if (!res.ok) throw new TransportError(fn, `HTTP ${res.status}`);

  let json;
  try {
    json = await inflateReply(await res.json());
  } catch (_) {
    throw new TransportError(fn, 'response was not JSON');
  }
  if (!isDoPostReply(json)) {
    throw new TransportError(fn, 'backend returned a health/redirect response instead of data');
  }
  if (json.error) {
    maybeForceReauth(json.error);          // expired session → back to sign-in
    maybeRecoverStrandedStaff(json.error); // non-staff Google sign-in → supplier login
    throw new Error(json.error);
  }
  return json.data;
}

/* ---------- normalizers ----------
 * Backend tables use snake_case + tab-specific id prefixes (vendor_id,
 * approval_id, invoice_id). Screens were built against the mock data's
 * camelCase shape. These helpers bridge the two without changing the sheet
 * schema or the screen components' field references. */

function normalizeVendor(r) {
  return {
    ...r,
    id: r.vendor_id,
    taxId: r.tax_id,
    updated: relativeWhen(r.updated_at),
    creditLimit: Number(r.credit_limit) || 0,
  };
}
function normalizeApproval(r) {
  return {
    ...r,
    id: r.approval_id,
    name: r.vendor_name || r.vendor_id,
    submittedBy: shortName(r.submitted_by_email),
    role: r.role,
    when: relativeWhen(r.submitted_at),
    docs: Number(r.docs_count) || 0,
    cat: r.category,
    note: r.note,
  };
}
function normalizeInvoice(r) {
  // attachment_ids is JSON-encoded [{id,name,size,mime}, ...] starting v21.
  // Older rows may hold a free-text rejection note (pre-fix) — those parse as
  // [] silently and the value is preserved as `rejection_note_legacy` for ref.
  let popAttachments = [];
  let legacyNote = null;
  if (r.attachment_ids) {
    try {
      const parsed = JSON.parse(r.attachment_ids);
      if (Array.isArray(parsed)) popAttachments = parsed;
    } catch (_) {
      legacyNote = String(r.attachment_ids);
    }
  }
  // Supplier-uploaded tax invoice files (post-payment), JSON like the POP set.
  let taxAttachments = [];
  if (r.tax_invoice_ids) {
    try {
      const parsed = JSON.parse(r.tax_invoice_ids);
      if (Array.isArray(parsed)) taxAttachments = parsed;
    } catch (_) { /* unreadable — treat as none */ }
  }
  return {
    ...r,
    id: r.invoice_id,
    vendor: r.vendor_name || r.vendor_id,
    vid: r.vendor_id,
    po: r.po_number,
    amount: Number(r.amount_thb) || 0,
    submitted: r.submitted_at,
    due: r.due_at,
    owner: shortName(r.pm_email),
    flag: null,
    age: relativeWhen(r.submitted_at),
    // Pull pdf metadata up for display
    pdf_filename: r.pdf_filename || '',
    pdf_size: Number(r.pdf_size) || 0,
    has_pdf: !!r.invoice_pdf_id,
    // Proof-of-performance attachments (parsed)
    pop_attachments: popAttachments,
    pop_count: popAttachments.length,
    // Tax invoice files (parsed)
    tax_attachments: taxAttachments,
    tax_count: taxAttachments.length,
    rejection_note: r.rejection_note || legacyNote || '',
  };
}

function shortName(email) {
  if (!email) return '';
  const local = String(email).split('@')[0];
  return local.split('.').map(s => s[0]?.toUpperCase() + s.slice(1)).join(' ');
}
function relativeWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const diffH = (Date.now() - d.getTime()) / 36e5;
  if (diffH < 1)  return `${Math.max(1, Math.round(diffH * 60))}m`;
  if (diffH < 24) return `${Math.round(diffH)}h`;
  if (diffH < 24 * 14) return `${Math.round(diffH / 24)}d`;
  return d.toLocaleDateString();
}

function camelize(arr, fn) { return Array.isArray(arr) ? arr.map(fn) : arr; }

/** A bootstrap section is either rows or { __error }. Normalize only rows. */
function section(value, normalizer) {
  if (!Array.isArray(value)) return value || null;   // null / { __error } passes through
  return normalizer ? value.map(normalizer) : value;
}

export const api = {
  whoami:         () => call('api_whoami', {}),

  /**
   * Every internal table in ONE round trip. Replaces the seven-call burst the
   * app used to fire on load and after every write — each of those cost ~2s of
   * GAS transport before its handler even started, and overlapping heavy reads
   * are what made random endpoints drop their response.
   *
   * `include` narrows the payload (e.g. ['invoices'] after a status change).
   * Sections that failed come back as { __error } so a caller can keep what it
   * already had rather than blanking the screen.
   */
  bootstrap: async (include) => {
    const raw = await call('api_bootstrap', include ? { include } : {});
    if (!raw) return null;
    return {
      whoami:     raw.whoami || null,
      sections:   raw.sections || [],
      vendors:    section(raw.vendors,    normalizeVendor),
      invoices:   section(raw.invoices,   normalizeInvoice),
      approvals:  section(raw.approvals,  normalizeApproval),
      ratings:    section(raw.ratings,    normalizeRating),
      compliance: section(raw.compliance, normalizeCompliance),
      users:      section(raw.users,      null),
      audit:      section(raw.audit,      normalizeAudit),
    };
  },

  // Supplier accounts (email + password). login / requestPasswordReset are
  // anonymous (no token). setPasswordWithToken passes the set/reset-link token
  // explicitly. changePassword uses the active session token.
  login:                (email, password, remember = true) =>
    callWith('api_login', { email, password, remember }, null),
  requestPasswordReset: (email) => callWith('api_requestPasswordReset', { email }, null),
  setPasswordWithToken: (token, password) => callWith('api_setPassword', { password }, token),
  changePassword:       (current, newPassword) =>
    call('api_changePassword', { current, new: newPassword }),

  // Supplier team access (multiple logins per vendor)
  listTeam:           () => call('api_listTeam', {}),
  inviteTeammate:     (email, name, team_role) => call('api_inviteTeammate', { email, name, team_role }),
  setTeammateStatus:  (email, status) => call('api_setTeammateStatus', { email, status }),
  removeTeammate:     (email) => call('api_removeTeammate', { email }),

  // Phase 1
  listVendors:    async (q = {}) => camelize(await call('api_listVendors', q), normalizeVendor),
  getVendor:      async (id) => {
    const r = await call('api_getVendor', { id });
    return r ? normalizeVendor(r) : null;
  },
  registerVendor: (data) => call('api_registerVendor', data),
  updateVendor:   (vendor_id, fields) => call('api_updateVendor', { vendor_id, ...fields }),
  setVendorCompliance: (vendor_id, compliance) => call('api_setVendorCompliance', { vendor_id, compliance }),
  // Soft-archive / restore a supplier (admin only). unarchive:true restores it.
  archiveVendor:  (vendor_id, opts = {}) => call('api_archiveVendor', { vendor_id, ...opts }),
  // Permanent delete (admin only) — backend refuses if the vendor has invoices.
  deleteVendor:   (vendor_id) => call('api_deleteVendor', { vendor_id }),
  listApprovals:  async (q = {}) => camelize(await call('api_listApprovals', q), normalizeApproval),
  decideApproval: (vendor_id, decision, reason) => call('api_decideApproval', { vendor_id, decision, reason }),
  listInvoices:   async (q = {}) => camelize(await call('api_listInvoices', q), normalizeInvoice),
  // company (brandstorm|motionreach) is required only when status === 'approved-payment'
  // — it routes the invoice into that company's Account Payable sheet. category is
  // the Finance-chosen Expense Main Category written into that sheet's Category column.
  // scheduled_date (YYYY-MM-DD) is required when status === 'scheduled' — it's the
  // payment date, written into the AP sheet's Due Date column.
  // Returns { ok, invoice_id, status, invoice } — `invoice` is the updated row,
  // already shaped like a list row, so the caller can move it in the UI without
  // waiting on a re-fetch of the whole billing table.
  setInvoiceStatus: async (id, status, note, company, category, scheduled_date) => {
    const r = await call('api_setInvoiceStatus', { id, status, note, company, category, scheduled_date });
    if (r && r.invoice) r.invoice = normalizeInvoice(r.invoice);
    return r;
  },
  // Edit an invoice's submitted details (amount/VAT/WHT/dates/invoice-no/project;
  // staff may also pass category). Backend gates who can edit and in which status.
  updateInvoiceDetails: (id, fields) => call('api_updateInvoiceDetails', { id, ...fields }),
  // Permanently delete an invoice (admin only) — also removes its AP row and
  // trashes its Drive files. Used to clean up test / bad submissions.
  deleteInvoice:  (invoice_id) => call('api_deleteInvoice', { invoice_id }),
  // Approved Expense Main Category list for a company, read live from that finance
  // sheet's "Expense Categories" tab. Returns { company, categories: [...] }.
  listExpenseCategories: (company) => call('api_listExpenseCategories', { company }),

  // Phase 2 — supplier portal
  portalMe:       async () => {
    const r = await call('api_portalMe', {});
    return r ? normalizeVendor(r) : null;
  },
  /**
   * Profile + own invoices in ONE round trip (2026-09-16). Sections that failed
   * come back as { __error }; the caller keeps what it already had for those.
   */
  portalBootstrap: async (include) => {
    const raw = await call('api_portalBootstrap', include ? { include } : {});
    if (!raw) return null;
    return {
      whoami:   raw.whoami || null,
      sections: raw.sections || [],
      vendor:   raw.vendor && !raw.vendor.__error ? normalizeVendor(raw.vendor) : (raw.vendor || null),
      invoices: section(raw.invoices, normalizeInvoice),
      compliance: raw.compliance,
    };
  },
  // One file → one request. Returns { id, name, size, mime }; the id is what
  // submitInvoice / uploadTaxInvoice take instead of base64 (see upload.js).
  uploadFile:     ({ purpose, base64, filename, vendor_id }) =>
    call('api_uploadFile', { purpose, base64, filename, ...(vendor_id ? { vendor_id } : {}) }),
  updatePortalProfile: (fields) => call('api_updatePortalProfile', fields),
  listMyCompliance: () => call('api_listMyCompliance', {}),
  uploadKycDoc: (doc_type, base64, filename, expires_at) =>
    call('api_uploadKycDoc', { doc_type, base64, filename, expires_at }),
  submitInvoice:  (data) => call('api_submitInvoice', data),
  issueMagicLink: (vendor_id, opts = {}) => call('api_issueMagicLink', { vendor_id, ...opts }),
  // KYC-submission link — portal link that lands the supplier on the document
  // upload screen. Emails the supplier by default (pass skip_email to opt out).
  issueKycLink: (vendor_id, opts = {}) => call('api_issueMagicLink', { vendor_id, purpose: 'kyc-submit', ...opts }),
  getInvoiceDocument: (invoice_id) => call('api_getInvoiceDocument', { invoice_id }),
  getInvoiceAttachment: (invoice_id, attachment_id) =>
    call('api_getInvoiceAttachment', { invoice_id, attachment_id }),
  // Tax invoice / official receipt for a PAID invoice. files: [{base64, filename}]
  // (legacy) or file_ids: [id] from uploadFile. Portal: own invoices only.
  uploadTaxInvoice: (invoice_id, files, file_ids) =>
    call('api_uploadTaxInvoice', file_ids ? { invoice_id, file_ids } : { invoice_id, files }),

  // Phase 3 — Audit + Ratings + Compliance
  listAudit:      async (q = {}) => {
    const rows = await call('api_listAudit', q);
    return Array.isArray(rows) ? rows.map(normalizeAudit) : rows;
  },
  listRatings:    async (q = {}) => {
    const rows = await call('api_listRatings', q);
    return Array.isArray(rows) ? rows.map(normalizeRating) : rows;
  },
  submitRating:   (data) => call('api_submitRating', data),
  listCompliance: async (q = {}) => {
    const rows = await call('api_listCompliance', q);
    return Array.isArray(rows) ? rows.map(normalizeCompliance) : rows;
  },

  // Phase 4 — admin actions
  addUser:                (data) => call('api_addUser', data),
  listUsers:              (q = {}) => call('api_listUsers', q),
  batchScheduleInvoices:  (invoice_ids, scheduled_date) =>
    call('api_batchScheduleInvoices', { invoice_ids, scheduled_date }),
};

/**
 * Map backend audit rows (snake_case + action like "approval.approved") into
 * the shape ScreenAudit's prototype expects.
 */
function normalizeAudit(r) {
  const at = r.at ? new Date(r.at) : null;
  const today = new Date(); today.setHours(0,0,0,0);
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  let dateLabel = '';
  if (at) {
    const d = new Date(at); d.setHours(0,0,0,0);
    if (d.getTime() === today.getTime()) dateLabel = 'Today';
    else if (d.getTime() === yesterday.getTime()) dateLabel = 'Yesterday';
    else dateLabel = at.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
  // Map action codes (vendor.register, approval.approved, invoice.submit, …)
  // to the simplified actions the prototype filter chips use.
  const a = String(r.action || '');
  let actionKey = 'edit';
  if (a.endsWith('.approved') || a === 'approval.approved' || a === 'invoice.approved-payment' || a === 'invoice.finance-verify') actionKey = 'approve';
  else if (a.endsWith('.rejected') || a === 'invoice.rejected') actionKey = 'reject';
  else if (a.endsWith('.register') || a.endsWith('.submit') || a.endsWith('.issue')) actionKey = 'create';
  else if (a.endsWith('.upload')) actionKey = 'upload';
  else if (a.endsWith('.login')) actionKey = 'login';

  // Try to summarize the diff for the "before/after" panel
  let before = null, after = null;
  try { if (r.before_json) before = JSON.stringify(JSON.parse(r.before_json), null, 2); } catch {}
  try { if (r.after_json)  after  = JSON.stringify(JSON.parse(r.after_json),  null, 2); } catch {}

  return {
    ...r,
    t: at ? at.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '',
    date: dateLabel,
    actor: shortName(r.actor_email_or_token) || r.actor_email_or_token || '—',
    role: r.context === 'portal' ? 'Vendor' : (r.context || 'staff'),
    action: actionKey,
    rawAction: a,
    entity: r.entity_id || r.entity_type || '—',
    target: r.entity_type || '',
    note: '',
    ip: r.ip_hint || '—',
    before, after,
  };
}

function normalizeRating(r) {
  return {
    ...r,
    id: r.rating_id,
    vendor: r.vendor_name || r.vendor_id,
    when: relativeWhen(r.rated_at),
    rater: shortName(r.rated_by_email),
  };
}
function normalizeCompliance(r) {
  return {
    ...r,
    id: r.compliance_id,
    vendor: r.vendor_name || r.vendor_id,
    issued: r.issued_at,
    expires: r.expires_at,
  };
}

/**
 * Build a CSV from rows + column specs and trigger a browser download.
 * columns: [{ label, key } | { label, get: row => value }]. A UTF-8 BOM is
 * prepended so Excel renders Thai text correctly.
 */
export function downloadCsv(filename, rows, columns) {
  const esc = (val) => {
    const s = val == null ? '' : String(val);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = columns.map(c => esc(c.label)).join(',');
  const body = (rows || []).map(r =>
    columns.map(c => esc(c.get ? c.get(r) : r[c.key])).join(',')
  ).join('\n');
  const csv = '﻿' + header + '\n' + body;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Read a File as base64 (no data: prefix). For invoice PDFs ≤ 20MB. */
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => resolve(String(reader.result).split(',')[1] || '');
    reader.onerror = () => reject(reader.error || new Error('file read failed'));
    reader.readAsDataURL(file);
  });
}

/** Format a byte count as "1.8 MB", "165 KB", etc. */
export function formatBytes(n) {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

/** Fetch a KYC compliance doc from the backend and trigger a browser download. */
export async function downloadComplianceDocument(compliance_id) {
  const doc = await call('api_getComplianceDocument', { compliance_id });
  if (!doc || !doc.base64) throw new Error('no document content');
  const bin  = atob(doc.base64);
  const buf  = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  const blob = new Blob([buf], { type: doc.mime_type || 'application/pdf' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url;
  a.download = doc.filename || `${compliance_id}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Fetch the invoice's PDF from the backend and trigger a browser download. */
export async function downloadInvoiceDocument(invoice_id) {
  const doc = await api.getInvoiceDocument(invoice_id);
  if (!doc || !doc.base64) throw new Error('no document content');
  triggerBlobDownload(doc, `${invoice_id}.pdf`, 'application/pdf');
}

/** Fetch one POP attachment and trigger a browser download. */
export async function downloadInvoiceAttachment(invoice_id, attachment_id, fallbackName) {
  const doc = await api.getInvoiceAttachment(invoice_id, attachment_id);
  if (!doc || !doc.base64) throw new Error('no attachment content');
  triggerBlobDownload(doc, fallbackName || `${invoice_id}-${attachment_id}`, 'application/octet-stream');
}

function triggerBlobDownload(doc, fallbackName, fallbackMime) {
  const bin  = atob(doc.base64);
  const buf  = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  const blob = new Blob([buf], { type: doc.mime_type || fallbackMime });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url;
  a.download = doc.filename || fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ---------- in-memory mock (used when VITE_GAS_API_URL is unset) ----------

const mockState = {
  vendors:   [...SUPPLIERS],
  approvals: [...PENDING_APPROVALS],
  invoices:  [...MY_BILLS],
};

async function mockCall(fn, args) {
  await new Promise(r => setTimeout(r, 80));
  switch (fn) {
    case 'api_listVendors':    return mockState.vendors;
    case 'api_getVendor':      return mockState.vendors.find(v => v.id === args.id);
    case 'api_listApprovals':  return mockState.approvals;
    case 'api_listInvoices':   return mockState.invoices;
    default:
      console.warn(`[mock api] ${fn} not implemented, returning []`);
      return [];
  }
}
